/**
 * Outlier rejection for GNSS fixes (feedback fb-mrvroxbq-3: "if we get one off
 * anomalous GNSS position or accuracy is terrible can we skip those off the
 * track").
 *
 * A real receiver occasionally spits out a single bad fix: a multipath spike in a
 * marina, a cold-start wander, or a fix with a huge reported error. Left alone it
 * lands on the recorded track as a spike and yanks the boat marker across the
 * chart for a second. This decides whether a fix looks like one of those glitches
 * so the ingest can hold it off the track (and hold the marker) rather than draw
 * it.
 *
 * The thresholds are deliberately generous, because a false reject (dropping a
 * real position) is worse than the occasional glitch slipping through:
 *
 *   - Accuracy: reject only when the receiver's own horizontal error estimate is
 *     terrible. This sits well above the poor-accuracy *warning* threshold
 *     (~15 m): between the two we still use the fix and just flag it; only a
 *     genuinely useless fix is dropped from the track.
 *
 *   - Teleport: reject when the implied speed from the previous good fix is
 *     faster than any vessel we chart for could possibly go. Set high on purpose
 *     so a fast RIB or ferry is never mistaken for a glitch; nothing real does
 *     60 kn between two one-second fixes.
 *
 * Pure, so the thresholds are testable without a live feed.
 */
import { rhumbDistanceNm } from '@/nav';

/** Horizontal accuracy (m) past which a fix is too poor to trust on the track. */
export const ACCURACY_REJECT_M = 50;

/** Implied speed (kn) past which a jump between fixes is a glitch, not movement. */
export const MAX_PLAUSIBLE_SPEED_KN = 60;

/**
 * Absolute jump cap (nm) used only when two fixes carry no usable time gap (same
 * timestamp), so a speed can't be derived. At 60 kn a one-second fix moves about
 * 0.017 nm, so a jump past a nautical mile with no time between fixes is clearly
 * a glitch.
 */
export const MAX_JUMP_NM = 1.0;

/**
 * How many bad fixes in a row we'll hold before accepting anyway. One glitch is
 * dropped cleanly; but if the "bad" position persists, the receiver has genuinely
 * relocated (out of a tunnel, powered up somewhere new), so we let it back in
 * rather than freezing the boat forever.
 */
export const MAX_CONSEC_REJECTS = 3;

export interface FixSample {
  position: [number, number];
  /** Arrival time, ms (wall clock is fine — we only use the gap between fixes). */
  tMs: number;
  /** Reported horizontal accuracy, m, if the receiver gave one. */
  accuracyM?: number;
}

export type FixRejectReason = 'accuracy' | 'teleport';

/**
 * Why this fix should be kept off the track, or null to accept it. `prev` is the
 * last good (accepted) fix, or null when we've no reference yet.
 */
export function fixRejectReason(prev: FixSample | null, next: FixSample): FixRejectReason | null {
  if (next.accuracyM != null && next.accuracyM > ACCURACY_REJECT_M) return 'accuracy';
  if (!prev) return null;
  const distNm = rhumbDistanceNm(prev.position, next.position);
  const dtSec = (next.tMs - prev.tMs) / 1000;
  if (dtSec > 0.001) {
    const speedKn = distNm / (dtSec / 3600);
    if (speedKn > MAX_PLAUSIBLE_SPEED_KN) return 'teleport';
  } else if (distNm > MAX_JUMP_NM) {
    return 'teleport';
  }
  return null;
}
